import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getLoyaltyCouponMismatchUsers } from "../api/AiAgentApi";

const formatReadableNumber = (value) => Number(value || 0).toLocaleString("ru-RU").replace(/\u00A0/g, " ");

export const LoyaltyCouponMismatchUsersPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(async ({ refresh = false } = {}) => {
    try {
      setError("");
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await getLoyaltyCouponMismatchUsers({ refresh });
      setItems(Array.isArray(response?.users) ? response.users : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Не удалось загрузить список пользователей с расхождениями.");
      setItems([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = useMemo(() => items, [items]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1.5} sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Пользователи с расхождением купонов
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={() => fetchData({ refresh: true })}
            disabled={loading || refreshing}
          >
            {refreshing ? "Обновляем..." : "Обновить кэш"}
          </Button>
          <Button variant="outlined" onClick={() => navigate("/loyalty-logs")}>Назад к логам</Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 3 }}>
        Кэш обновляется один раз в день в 04:00 по Москве (МСК).
      </Alert>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Как считаются поля
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          Соответствие API-полей и колонок таблицы:
          {" "}hookahsSpent → «Потрачено кальянов»,
          {" "}expectedCouponsByHookahs → «Ожидаемо купонов»,
          {" "}couponsIssuedByEmployee → «Выдано сотрудником».
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>
            <Typography variant="body2">
              <b>hookahsSpent</b> — сколько кальянов у пользователя списано за все время
              (из логов <code>UPDATE_COUNT_HOOKAHS</code> с <code>value &lt; 0</code>, сумма модулей).
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <b>expectedCouponsByHookahs</b> — сколько купонов должно быть выдано по правилу
              <b> 1 купон за 6 списанных кальянов</b>: <code>hookahsSpent / 6</code> (целочисленное деление).
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <b>couponsIssuedByEmployee</b> — сколько купонов фактически выдал сотрудник
              (из логов <code>UPDATE_COUNT_COUPON</code>, где <code>value &gt; 0</code> и <code>employee != null</code>).
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          Пользователь попадает в список, если{" "}
          <b>expectedCouponsByHookahs != couponsIssuedByEmployee</b>.
        </Typography>
      </Paper>

      {loading ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress size={26} />
        </Paper>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <Typography color="text.secondary">Расхождений не найдено.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID пользователя</TableCell>
                <TableCell>Имя</TableCell>
                <TableCell align="right">Потрачено кальянов</TableCell>
                <TableCell align="right">Ожидаемо купонов</TableCell>
                <TableCell align="right">Выдано сотрудником</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item, index) => {
                const fullName = [item?.user?.firstName, item?.user?.lastName].filter(Boolean).join(" ");
                return (
                  <TableRow key={`${item?.user?.id || "unknown"}-${index}`} hover>
                    <TableCell>{item?.user?.id ?? "—"}</TableCell>
                    <TableCell>{fullName || "Без имени"}</TableCell>
                    <TableCell align="right">{formatReadableNumber(item?.hookahsSpent)}</TableCell>
                    <TableCell align="right">{formatReadableNumber(item?.expectedCouponsByHookahs)}</TableCell>
                    <TableCell align="right">{formatReadableNumber(item?.couponsIssuedByEmployee)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
